import * as types from "../../mutation-types";
import BoardsAPI from "kanban/api/boards";
import PreferencesAPI from "kanban/api/preferences";
import TasksAPI from "kanban/api/tasks";
import ProductsAPI from "kanban/api/products";
import { isRestrictedKanbanViewer } from "kanban/helper/restrictedViewer";

// Per-board filter state persisted in the preferences jsonb.
const BOARD_FILTER_KEYS = {
	agentId: "agent_id",
	inboxId: "inbox_id",
	showCompleted: "show_completed",
	showCancelled: "show_cancelled",
	payload: "payload",
	q: "q",
	savedViewId: "saved_view_id",
	since: "since",
	until: "until",
	dateRangeType: "date_range_type",
};

function taskBelongsToSelectedBoard(state, task) {
	if (!state.selectedBoardId) return false;
	return String(task.board_id) === String(state.selectedBoardId);
}

// Realtime adds must respect the active board filters so filtered views do not
// drift. Advanced payload/q filters cannot be evaluated client-side — skip add.
function taskMatchesActiveFilters(state, task) {
	const boardFilters =
		state.preferences.board_filters?.[state.selectedBoardId] || {};
	const {
		agent_id: agentId,
		inbox_id: inboxId,
		payload,
		q,
		since,
		until,
	} = boardFilters;

	if (payload?.length || q) return false;

	if (agentId && agentId !== "all") {
		const assigneeIds = (task.assigned_agents || []).map((a) => String(a.id));
		if (!assigneeIds.includes(String(agentId))) return false;
	}

	if (inboxId && inboxId !== "all") {
		const inboxIds = (task.conversations || []).map((c) => String(c.inbox_id));
		if (!inboxIds.includes(String(inboxId))) return false;
	}

	if (since || until) {
		const activityRaw = task.step_changed_at || task.created_at;
		const activityAt = toUnixSeconds(activityRaw);
		if (activityAt == null) return false;
		if (since && activityAt < Number(since)) return false;
		// `until` is exclusive (start of the day after the selected end date).
		if (until && activityAt >= Number(until)) return false;
	}

	return true;
}

function toUnixSeconds(value) {
	if (value == null || value === "") return null;
	if (typeof value === "number") {
		return value < 1e12 ? value : value / 1000;
	}
	const ms = new Date(value).getTime();
	return Number.isFinite(ms) ? ms / 1000 : null;
}

function taskExistsInState(state, taskId) {
	return Object.keys(state.stepTasks).some((stepId) =>
		state.stepTasks[stepId]?.some((t) => t.id === taskId),
	);
}

export default {
	async fetchBoards({ commit, state, rootGetters }, params = {}) {
		const accountId = rootGetters.getCurrentAccountId;
		if (state.boardsAccountId !== accountId) {
			commit(types.RESET_KANBAN_ACCOUNT_STATE, accountId);
		}

		commit(types.INCREMENT_BOARDS_REQUEST_VERSION);
		const requestVersion = state.boardsRequestVersion;
		commit(types.SET_KANBAN_LOADING, true);
		commit(types.SET_BOARDS_LOADED, false);
		commit(types.SET_BOARDS_FETCH_FAILED, false);
		try {
			const response = await BoardsAPI.get(params);
			if (requestVersion !== state.boardsRequestVersion) return true;

			const { boards, preferences } = response.data;
			if (preferences) {
				commit(types.SET_KANBAN_PREFERENCES, preferences);
			}
			commit(types.SET_BOARDS, boards);
			return true;
		} catch {
			if (requestVersion !== state.boardsRequestVersion) return true;

			commit(types.SET_BOARDS_FETCH_FAILED, true);
			return false;
		} finally {
			if (requestVersion === state.boardsRequestVersion) {
				commit(types.SET_KANBAN_LOADING, false);
				commit(types.SET_BOARDS_LOADED, true);
			}
		}
	},

	async fetchSteps({ commit, state }, { boardId, agentId, inboxId } = {}) {
		const targetBoardId = boardId || state.selectedBoardId;
		if (!targetBoardId) return;

		// Column counts have to come from the same conditions as the card list.
		// fetchTasksForStep reads the persisted filters, so fall back to them when
		// the caller passes nothing (deleteStep, for one) or the badges drift away
		// from the cards. An explicit argument still wins: KanbanBoardPage forces
		// 'all' for viewers restricted to their own cards.
		const boardFilters = state.preferences.board_filters?.[targetBoardId] || {};
		const { payload, q, since, until } = boardFilters;
		const hasAdvancedFilter =
			Boolean(payload?.length) || Boolean(q) || Boolean(since) || Boolean(until);
		const effectiveAgentId = agentId ?? boardFilters.agent_id;
		const effectiveInboxId = inboxId ?? boardFilters.inbox_id;

		// Overlapping reloadBoard() calls (change the filter, then the agent) put
		// two step requests in flight for the same board, and the older one must
		// not commit its counts on top of the newer. fetchTasksForStep guards the
		// cards the same way; without this the badges would be the ones drifting.
		commit(types.INCREMENT_STEPS_REQUEST_VERSION);
		const requestVersion = state.stepsRequestVersion;

		try {
			const response = hasAdvancedFilter
				? await BoardsAPI.filterSteps(targetBoardId, {
						agentId: effectiveAgentId,
						inboxId: effectiveInboxId,
						payload,
						q,
						since,
						until,
					})
				: await BoardsAPI.getSteps(targetBoardId, {
						agentId: effectiveAgentId,
						inboxId: effectiveInboxId,
						since,
						until,
					});
			if (
				state.selectedBoardId === targetBoardId &&
				state.stepsRequestVersion === requestVersion
			) {
				commit(types.SET_STEPS, response.data.steps);
			}
		} catch {
			// Ignore error
		}
	},

	async setActiveBoard({ commit, dispatch }, { boardId, agentId, inboxId }) {
		commit(types.SET_KANBAN_LOADING, true);
		commit(types.SET_SELECTED_BOARD_ID, boardId);
		// Clear steps immediately to prevent stale data triggering task fetches
		commit(types.SET_STEPS, []);
		try {
			// Only fetch steps - tasks will be fetched per step by the component
			await dispatch("fetchSteps", { boardId, agentId, inboxId });
		} finally {
			commit(types.SET_KANBAN_LOADING, false);
		}
	},

	async toggleFavoriteBoard({ commit, state }, boardId) {
		const previousFavorites = [...(state.preferences.favorite_board_ids || [])];
		let newFavorites;

		if (previousFavorites.includes(boardId)) {
			newFavorites = previousFavorites.filter((id) => id !== boardId);
		} else {
			newFavorites = [...previousFavorites, boardId];
		}

		commit(types.SET_KANBAN_PREFERENCES, {
			...state.preferences,
			favorite_board_ids: newFavorites,
		});

		try {
			const response = await BoardsAPI.toggleFavorite(boardId);
			const { favorite_board_ids } = response.data;

			commit(types.SET_KANBAN_PREFERENCES, {
				...state.preferences,
				favorite_board_ids,
			});
		} catch {
			commit(types.SET_KANBAN_PREFERENCES, {
				...state.preferences,
				favorite_board_ids: previousFavorites,
			});
		}
	},

	async createBoard({ commit }, boardData) {
		const response = await BoardsAPI.create(boardData);
		commit(types.ADD_BOARD, response.data);
		return response.data;
	},

	async createTask({ commit }, taskData) {
		const response = await TasksAPI.create(taskData);
		commit(types.ADD_TASK, response.data);
		return response.data;
	},

	async updateTask({ commit, state }, { id, task }) {
		// Find task in stepTasks
		let originalTask = null;
		const stepIds = Object.keys(state.stepTasks);
		stepIds.some((stepId) => {
			originalTask = state.stepTasks[stepId]?.find((t) => t.id === id);
			return !!originalTask;
		});

		if (originalTask) {
			const updates = task ? { ...task } : {};
			const optimisticTask = { ...originalTask, ...updates };
			commit(types.UPDATE_TASK, optimisticTask);
		}

		try {
			const payload = { task };
			const response = await TasksAPI.update(id, payload);
			commit(types.UPDATE_TASK, response.data);
			return response.data;
		} catch (error) {
			if (originalTask) {
				commit(types.UPDATE_TASK, originalTask);
			}
			throw error;
		}
	},

	async moveTask(
		{ commit, state, dispatch },
		{ taskId, destinationStepId, insertBeforeTaskId, refreshStepId = null },
	) {
		// Find task in stepTasks
		let task = null;
		const stepIds = Object.keys(state.stepTasks);
		stepIds.some((stepId) => {
			task = state.stepTasks[stepId]?.find((t) => t.id === taskId);
			return !!task;
		});
		if (!task) return;

		const sourceStepId = task.board_step_id;
		const sourceStep = state.steps.find((s) => s.id === sourceStepId);
		const destinationStep = state.steps.find((s) => s.id === destinationStepId);

		if (!sourceStep || !destinationStep) return;

		const getTasksOrder = (stepId) =>
			state.preferences.tasks_order?.[stepId] || [];

		let sourceStepTasksOrder = [...getTasksOrder(sourceStepId)];
		sourceStepTasksOrder = sourceStepTasksOrder.filter((id) => id !== taskId);

		let destinationStepTasksOrder;
		if (sourceStepId === destinationStepId) {
			destinationStepTasksOrder = [...sourceStepTasksOrder];
		} else {
			destinationStepTasksOrder = [...getTasksOrder(destinationStepId)];
		}

		if (insertBeforeTaskId) {
			const index = destinationStepTasksOrder.indexOf(insertBeforeTaskId);
			if (index !== -1) {
				destinationStepTasksOrder.splice(index, 0, taskId);
			} else {
				destinationStepTasksOrder.push(taskId);
			}
		} else {
			destinationStepTasksOrder.push(taskId);
		}

		commit(types.MOVE_TASK, {
			task: { ...task, board_step_id: destinationStepId },
			sourceStepId,
			destinationStepId,
			insertBeforeTaskId,
		});

		const newPreferences = {
			...state.preferences,
			tasks_order: {
				...(state.preferences.tasks_order || {}),
				[sourceStepId]: sourceStepTasksOrder,
				[destinationStepId]: destinationStepTasksOrder,
			},
		};

		const originalPreferences = { ...state.preferences };
		commit(types.SET_KANBAN_PREFERENCES, newPreferences);

		try {
			await TasksAPI.move(taskId, {
				board_step_id: destinationStepId,
				insert_before_task_id: insertBeforeTaskId,
			});

			// Refresh step tasks after successful move (e.g., when sorting by last activity)
			if (refreshStepId) {
				dispatch("fetchTasksForStep", {
					stepId: refreshStepId,
					page: 1,
					perPage: 10,
				});
			}
		} catch {
			// Revert based on original task state
			// We don't have the original insertBeforeTaskId easily available to restore exact position
			// but UPDATE_TASK will at least put it back in the correct step list
			commit(types.UPDATE_TASK, task);
			commit(types.SET_KANBAN_PREFERENCES, originalPreferences);
		}
	},

	async deleteTask({ commit }, id) {
		await TasksAPI.delete(id);
		commit(types.DELETE_TASK, id);
	},

	async updateStep({ commit, state }, { boardId, stepId, ...data }) {
		const originalStep = state.steps.find((s) => s.id === stepId);
		if (originalStep && data.step) {
			const optimisticStep = { ...originalStep, ...data.step };
			commit(types.UPDATE_STEP, optimisticStep);
		}

		try {
			const response = await BoardsAPI.updateStep(boardId, stepId, data);
			commit(types.UPDATE_STEP, response.data);
			return response.data;
		} catch (error) {
			if (originalStep) {
				commit(types.UPDATE_STEP, originalStep);
			}
			throw error;
		}
	},

	async deleteStep({ dispatch, commit }, { boardId, stepId }) {
		await BoardsAPI.deleteStep(boardId, stepId);
		// Reset step tasks and re-fetch steps
		commit(types.RESET_STEP_TASKS);
		await dispatch("fetchSteps", { boardId });
	},

	async createStep({ commit }, { boardId, ...stepData }) {
		const response = await BoardsAPI.createStep(boardId, stepData);
		commit(types.ADD_STEP, response.data);
		return response.data;
	},

	async updateBoard({ commit }, { id, board }) {
		const response = await BoardsAPI.update(id, { board });
		commit(types.UPDATE_BOARD, response.data);
		return response.data;
	},

	async updateBoardAgents({ commit }, { boardId, agentIds }) {
		const response = await BoardsAPI.updateAgents(boardId, agentIds);
		commit(types.UPDATE_BOARD, response.data);
		return response.data;
	},

	async updateBoardInboxes({ commit }, { boardId, inboxIds }) {
		const response = await BoardsAPI.updateInboxes(boardId, inboxIds);
		commit(types.UPDATE_BOARD, response.data);
		return response.data;
	},

	async deleteBoard({ commit }, boardId) {
		await BoardsAPI.delete(boardId);
		commit(types.DELETE_BOARD, boardId);
	},

	// Partial update: only the keys actually passed are touched, so persisting the
	// agent select can't wipe the advanced filter (and vice versa). An explicit
	// empty array or empty string still clears -- only `undefined` means "leave as is".
	async updateBoardFilters({ commit, state }, { boardId, ...changes }) {
		const currentFilters = state.preferences.board_filters?.[boardId] || {};

		const patch = Object.entries(BOARD_FILTER_KEYS).reduce(
			(acc, [camelKey, snakeKey]) => {
				if (changes[camelKey] !== undefined) acc[snakeKey] = changes[camelKey];
				return acc;
			},
			{},
		);

		const nextFilters = { ...currentFilters, ...patch };
		const preferencesToUpdate = { board_filters: { [boardId]: nextFilters } };

		commit(types.SET_KANBAN_PREFERENCES, {
			...state.preferences,
			board_filters: {
				...(state.preferences.board_filters || {}),
				[boardId]: nextFilters,
			},
		});

		try {
			await PreferencesAPI.update(preferencesToUpdate);
			return true;
		} catch {
			// Revert this board's filters only. A full snapshot taken before the
			// request would also undo any unrelated preference committed while it
			// was in flight (a favourited board, another board's filters).
			commit(types.SET_KANBAN_PREFERENCES, {
				...state.preferences,
				board_filters: {
					...(state.preferences.board_filters || {}),
					[boardId]: currentFilters,
				},
			});
			return false;
		}
	},

	addTaskFromEvent({ commit, state }, task) {
		if (!taskBelongsToSelectedBoard(state, task)) return;
		if (!taskMatchesActiveFilters(state, task)) return;
		commit(types.ADD_TASK, task);
	},

	updateTaskFromEvent({ commit, state }, task) {
		if (!taskBelongsToSelectedBoard(state, task)) {
			if (taskExistsInState(state, task.id)) {
				commit(types.DELETE_TASK, task.id);
			}
			return;
		}

		// Check if task moved to a different step
		let currentStepId = null;
		const stepIds = Object.keys(state.stepTasks);
		stepIds.some((stepId) => {
			const found = state.stepTasks[stepId]?.find((t) => t.id === task.id);
			if (found) {
				currentStepId = stepId;
				return true;
			}
			return false;
		});

		if (!currentStepId) {
			if (taskMatchesActiveFilters(state, task)) {
				commit(types.ADD_TASK, task);
			}
			return;
		}

		if (!taskMatchesActiveFilters(state, task)) {
			commit(types.DELETE_TASK, task.id);
			return;
		}

		const newStepId = task.board_step_id;
		const stepChanged =
			currentStepId &&
			String(currentStepId) !== String(newStepId) &&
			currentStepId !== newStepId;

		// Check if this is a move operation (has explicit position info)
		// insert_before_task_id key is only present during move operations
		const hasMovePositionInfo = Object.hasOwn(task, "insert_before_task_id");

		// If we have position info, use MOVE_TASK (works for both between-step and within-step moves)
		if (hasMovePositionInfo && currentStepId) {
			commit(types.MOVE_TASK, {
				task: { ...task, board_step_id: newStepId },
				sourceStepId: currentStepId,
				destinationStepId: newStepId,
				insertBeforeTaskId: task.insert_before_task_id,
			});
		} else if (stepChanged) {
			// Step changed but no position info - use MOVE_TASK, will append to end
			commit(types.MOVE_TASK, {
				task: { ...task, board_step_id: newStepId },
				sourceStepId: currentStepId,
				destinationStepId: newStepId,
				insertBeforeTaskId: null,
			});
		} else {
			// Same step, no position info - just update properties in place
			commit(types.UPDATE_TASK, task);
		}
	},

	deleteTaskFromEvent({ commit }, taskId) {
		commit(types.DELETE_TASK, taskId);
	},

	addStepFromEvent({ commit }, step) {
		commit(types.ADD_STEP, step);
	},

	updateStepFromEvent({ commit }, step) {
		commit(types.UPDATE_STEP, step);
	},

	updateBoardFromEvent({ commit, state, dispatch, rootGetters }, board) {
		// The board.updated broadcast carries global aggregates. For a restricted
		// agent (including custom role), keep the viewer-scoped totals already in
		// state instead of overwriting them with the global ones from the event.
		const isRestricted = isRestrictedKanbanViewer(
			board,
			rootGetters.getCurrentRole,
		);

		const existing = isRestricted
			? state.boards.find((b) => b.id === board.id)
			: null;

		// Cached totals are only viewer-scoped when the board was ALREADY
		// restricted. If it just flipped unrestricted -> restricted, those cached
		// totals are still global, so preserving them would leave stale counts.
		const wasRestricted =
			existing?.settings?.restrict_agent_view_to_own_tasks === true;

		if (existing && wasRestricted) {
			commit(types.UPDATE_BOARD, {
				...board,
				total_tasks_count: existing.total_tasks_count,
				total_value: existing.total_value,
				total_weighted_value: existing.total_weighted_value,
				steps_summary: existing.steps_summary,
			});
			return;
		}

		if (isRestricted) {
			// Unrestricted -> restricted transition: drop the event's global totals
			// and refetch viewer-scoped stats so the agent doesn't see stale counts.
			commit(types.UPDATE_BOARD, {
				...board,
				total_tasks_count: undefined,
				total_value: undefined,
				total_weighted_value: undefined,
				steps_summary: undefined,
			});
			dispatch("fetchBoards");
			return;
		}

		commit(types.UPDATE_BOARD, board);
	},

	// Step-based task loading actions
	async fetchTasksForStep(
		{ commit, state },
		{ stepId, page = 1, perPage = 10, append = false },
	) {
		// Block all concurrent requests while loading (prevents rapid scroll from queueing multiple requests)
		if (state.stepLoading[stepId]) return;

		// Increment request version to invalidate any pending requests
		commit(types.INCREMENT_STEP_REQUEST_VERSION, stepId);
		const requestVersion = state.stepRequestVersion[stepId];

		commit(types.SET_STEP_LOADING, { stepId, isLoading: true });

		try {
			const activeBoardId = state.selectedBoardId;
			const savedSort = state.preferences.task_sorting?.[activeBoardId] || {};
			const sort = savedSort.sort || "position";
			const order = savedSort.order || "asc";

			const boardFilters =
				state.preferences.board_filters?.[activeBoardId] || {};
			const agentId = boardFilters.agent_id;
			const inboxId = boardFilters.inbox_id;
			const since = boardFilters.since;
			const until = boardFilters.until;

			const requestParams = {
				page,
				perPage,
				sort: sort === "position" ? undefined : sort,
				order: sort === "position" ? undefined : order,
				agentId: agentId !== "all" ? agentId : undefined,
				inboxId: inboxId !== "all" ? inboxId : undefined,
				since,
				until,
			};

			const { payload, q } = boardFilters;
			const hasAdvancedFilter = Boolean(payload?.length) || Boolean(q);

			const response = hasAdvancedFilter
				? await TasksAPI.filterByStep(stepId, { ...requestParams, payload, q })
				: await TasksAPI.getByStep(stepId, requestParams);

			// Discard response if a newer request was made
			if (state.stepRequestVersion[stepId] !== requestVersion) {
				return;
			}

			const { tasks, meta } = response.data;

			if (append) {
				commit(types.APPEND_STEP_TASKS, { stepId, tasks });
			} else {
				commit(types.SET_STEP_TASKS, { stepId, tasks });
			}

			if (meta) {
				// Transform snake_case keys to camelCase
				// Safety: if API returns empty tasks but claims hasMore, force hasMore to false
				// This prevents infinite scroll loops when filters cause count mismatches
				const actualHasMore = meta.has_more && tasks.length > 0;
				const transformedMeta = {
					totalCount: meta.total_count,
					page: meta.page,
					perPage: meta.per_page,
					hasMore: actualHasMore,
				};
				commit(types.SET_STEP_META, { stepId, meta: transformedMeta });
			}
		} catch {
			// On error, set hasMore to false to prevent stuck infinite scroll
			commit(types.SET_STEP_META, {
				stepId,
				meta: { ...state.stepMeta[stepId], hasMore: false },
			});
		} finally {
			// Always clear loading state to prevent stuck states
			commit(types.SET_STEP_LOADING, { stepId, isLoading: false });
		}
	},

	async fetchMoreTasksForStep({ dispatch, state }, stepId) {
		const meta = state.stepMeta[stepId];
		if (!meta || !meta.hasMore) return;

		const nextPage = meta.page + 1;
		await dispatch("fetchTasksForStep", {
			stepId,
			page: nextPage,
			perPage: meta.perPage,
			append: true,
		});
	},

	async initializeStepTasks({ commit, dispatch }, { stepIds }) {
		commit(types.RESET_STEP_TASKS);

		// Fetch first page for each step in parallel
		await Promise.all(
			stepIds.map((stepId) =>
				dispatch("fetchTasksForStep", { stepId, page: 1, perPage: 10 }),
			),
		);
	},

	resetStepTasks({ commit }) {
		commit(types.RESET_STEP_TASKS);
	},

	async updateTaskSorting({ commit, state }, { boardId, sort, order }) {
		const previousPreferences = { ...state.preferences };
		const newPreferences = {
			...state.preferences,
			task_sorting: {
				...(state.preferences.task_sorting || {}),
				[boardId]: { sort, order },
			},
		};
		commit(types.SET_KANBAN_PREFERENCES, newPreferences);

		try {
			await PreferencesAPI.update({
				task_sorting: newPreferences.task_sorting,
			});
		} catch {
			commit(types.SET_KANBAN_PREFERENCES, previousPreferences);
		}
	},

	// Product catalog actions
	async fetchProducts({ commit, state, rootGetters }, { boardId }) {
		const accountId = rootGetters.getCurrentAccountId;
		try {
			const response = await ProductsAPI.get(boardId);
			if (
				state.boardsAccountId !== accountId ||
				rootGetters.getCurrentAccountId !== accountId
			)
				return;

			commit(types.SET_PRODUCTS, {
				boardId,
				products: response.data.products,
			});
		} catch {
			// Ignore error
		}
	},

	async createProduct({ commit }, { boardId, productData }) {
		const response = await ProductsAPI.create(boardId, productData);
		commit(types.ADD_PRODUCT, { boardId, product: response.data });
		return response.data;
	},

	async updateProduct({ commit }, { boardId, productId, productData }) {
		const response = await ProductsAPI.update(boardId, productId, productData);
		commit(types.UPDATE_PRODUCT, { boardId, product: response.data });
		return response.data;
	},

	async deleteProduct({ commit }, { boardId, productId }) {
		await ProductsAPI.delete(boardId, productId);
		commit(types.DELETE_PRODUCT, { boardId, productId });
	},
};
